class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'shopify_recently_viewed';
    this.maxProducts = 12;
    this.productsToShow = 4;
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    // Get configuration from data attributes
    this.maxProducts = parseInt(this.getAttribute('data-max-products') || '12', 10);
    this.productsToShow = parseInt(this.getAttribute('data-limit') || '4', 10);

    // Add current product first, then render
    this.handleCurrentProduct();

    // Small delay to ensure cookie is set before rendering
    setTimeout(() => {
      this.renderProducts();
    }, 50);
  }

  getCookie(name) {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [key, value] = cookie.trim().split('=');
      if (key === name) {
        return value || '';
      }
    }
    return '';
  }

  setCookie(name, value, days = 30) {
    // Session cookie (no expiration) with domain for all chestudios.co.uk subdomains
    document.cookie = `${name}=${value}; path=/; domain=.chestudios.co.uk; SameSite=Lax`;

  }

  addProductHandle(handle) {
    if (!handle) return;

    // Get current cookie value
    let currentValue = this.getCookie(this.cookieName);

    let handles = [];
    if (currentValue) {
      handles = currentValue.split(' ').filter(h => h && h.length > 0);
    }

    // Remove handle if it already exists
    handles = handles.filter(h => h !== handle);

    // Add new handle to the beginning
    handles.unshift(handle);

    // Limit to max products
    if (handles.length > this.maxProducts) {
      handles = handles.slice(0, this.maxProducts);
    }

    const newValue = handles.join(' ');

    // Set the cookie
    this.setCookie(this.cookieName, newValue);
  }

  getProductHandles() {
    const cookieValue = this.getCookie(this.cookieName);
    if (!cookieValue) {
      return [];
    }

    const handles = cookieValue.split(' ').filter(h => h && h.length > 0);
    return handles;
  }

  handleCurrentProduct() {
    const currentProductHandle = this.getAttribute('data-current-product');
    if (!currentProductHandle) {
      return;
    }

    this.addProductHandle(currentProductHandle);
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const value = parseInt(cents, 10);
    if (isNaN(value)) {
      return '$0.00';
    }

    const dollars = Math.floor(value / 100);
    const remainingCents = value % 100;
    return `$${dollars}.${remainingCents.toString().padStart(2, '0')}`;
  }

  async renderProducts() {
    const handles = this.getProductHandles();
    const currentProductHandle = this.getAttribute('data-current-product');

    // Filter out the current product from the display list
    const filteredHandles = handles.filter(handle => handle !== currentProductHandle);
    const displayHandles = filteredHandles.slice(0, this.productsToShow);

    // Clear content
    this.innerHTML = '';

    if (displayHandles.length === 0) {
      this.innerHTML = '<p class="text-center text-gray-500 col-span-full">No recently viewed products</p>';
      return;
    }

    // Create placeholder cards first
    displayHandles.forEach(() => {
      const placeholder = document.createElement('div');
      placeholder.className = 'relative group grid grid-cols-1 grid-rows-[min-content_1fr] gap-2';
      placeholder.innerHTML = '<div class="animate-pulse bg-gray-200 aspect-[var(--product-card-aspect-ratio)]"></div>';
      this.appendChild(placeholder);
    });

    try {
      // Use Search API with custom view for recently viewed products
      const searchQuery = displayHandles.map(handle => `handle:${handle}`).join(' OR ');
      const searchUrl = `/search?view=recently-viewed&type=product&q=${encodeURIComponent(searchQuery)}`;
      const response = await fetch(searchUrl);

      if (!response.ok) {
        throw new Error(`Search API failed: ${response.status}`);
      }

      const html = await response.text();

      // Create a temporary container to parse the HTML
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = html;

      // Look for product cards in the response
      const productCards = tempContainer.querySelectorAll('product-card');

      if (productCards.length > 0) {
        // Clear placeholders
        this.innerHTML = '';

        // Extract product handles from the returned HTML and sort them
        const foundProducts = [];
        productCards.forEach(card => {
          let handle = null;

          // Try to get handle from data attribute on product-card
          handle = card.getAttribute('data-product-handle');

          // If not found, try to extract from href within the product-card
          if (!handle) {
            const link = card.querySelector('a[href*="/products/"]');
            if (link && link.href) {
              const match = link.href.match(/\/products\/([^\/\?]+)/);
              if (match) handle = match[1];
            }
          }

          if (handle && displayHandles.includes(handle)) {
            foundProducts.push({ handle, element: card });
          }
        });

        // Sort products to match the order of displayHandles
        const sortedProducts = [];
        displayHandles.forEach(handle => {
          const product = foundProducts.find(p => p.handle === handle);
          if (product) sortedProducts.push(product);
        });

        // Append the sorted product cards
        sortedProducts.forEach(product => {
          const clonedCard = product.element.cloneNode(true);
          this.appendChild(clonedCard);
        });

        // If we didn't get all products, fill in with individual fetches
        if (sortedProducts.length < displayHandles.length) {
          await this.fillMissingProducts(displayHandles, sortedProducts.map(p => p.handle));
        }

      } else {
        throw new Error('No products found in search response');
      }

    } catch (error) {
      await this.renderIndividualProducts(displayHandles);
    }
  }

  async fillMissingProducts(allHandles, foundHandles) {
    const missingHandles = allHandles.filter(handle => !foundHandles.includes(handle));

    for (const handle of missingHandles) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) continue;

        const product = await response.json();

        // Create a simple fallback card for missing products
        const errorCard = document.createElement('div');
        errorCard.className = 'relative group grid grid-cols-1 grid-rows-[min-content_1fr] gap-2';
        errorCard.innerHTML = `
          <div class="aspect-[var(--product-card-aspect-ratio)] bg-gray-100 flex items-center justify-center">
            <p class="text-xs text-gray-400">Product: ${product.title}</p>
          </div>
        `;
        this.appendChild(errorCard);
      } catch (error) {
        // Silently fail for missing products
      }
    }
  }

  async renderIndividualProducts(displayHandles) {
    // Clear content
    this.innerHTML = '';

    // Show a simple fallback message since we can't use the Search API
    this.innerHTML = `
      <div class="col-span-full text-center p-4">
        <p class="text-gray-500">Recently viewed products temporarily unavailable</p>
        <p class="text-sm text-gray-400 mt-1">Search API not responding</p>
      </div>
    `;
  }
}

// Define the custom element
customElements.define('recently-viewed-products', RecentlyViewedProducts);
